// A person's own sign-in and their firm's people, shared by the two screens
// that show them: the staff portal's **Settings** (everybody's own two-step
// sign-in; a firm's owners and admins add and change their people) and the
// account console (`/console`, where a super admin does the same for any
// firm).
//
// Every control is offered from what `/accounts/me` says the session may do
// (`capabilities`), never from the role — the middleware's routes are the
// rule. Imports only `shared.js` and `staffAuth.js`, so `App.js` and
// `Console.js` can both use it without either importing the other. See
// docs/decisions/staff-sign-in.md.

import { useState, useEffect, useCallback } from "react";
import { T } from "./shared";
import {
  accountsFetch, isSessionToken, signOut, TwoFactorSetup, RecoveryCodes, Btn, Note, Label, inputStyle,
} from "./staffAuth";

export const ROLE_LABELS = { owner: "Owner", admin: "Admin", staff: "Staff" };
export const STATUS_LABELS = { active: "Active", invited: "Invited", removed: "Removed", suspended: "Suspended" };

export const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

// Two looks for one card: the console's own section, and a card on the
// Settings screen, which sits among the other settings cards (13px title,
// 12px line under it) rather than shouting over them.
export const Section = ({ title, hint, action, children, look = "console", maxWidth = 900 }) => (look === "settings" ? (
  <section style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, maxWidth, marginBottom: 20 }}>
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 3 }}>{title}</div>
        {hint && <p style={{ fontSize: 12, color: T.textFaint, margin: 0 }}>{hint}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
) : (
  <section style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: 24, marginBottom: 20 }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
      <h2 style={{ margin: 0, fontWeight: 300, fontSize: 19, letterSpacing: "-0.02em", color: T.text }}>{title}</h2>
      {action}
    </div>
    {hint && <p style={{ fontSize: 13, color: T.textSoft, margin: "-6px 0 16px", lineHeight: 1.6 }}>{hint}</p>}
    {children}
  </section>
));

export const Badge = ({ children, tone = "ink" }) => {
  const colour = { ink: T.textSoft, red: T.danger, warn: T.warn }[tone];
  return (
    <span style={{ display: "inline-block", border: `1px solid ${colour}`, color: colour, background: T.white,
      borderRadius: 999, padding: "1px 8px", fontSize: 11.5, fontWeight: 500, whiteSpace: "nowrap" }}>{children}</span>
  );
};

const th = { textAlign: "left", fontSize: 11.5, fontWeight: 600, color: T.textFaint, padding: "8px 10px", borderBottom: `1px solid ${T.border}`, textTransform: "uppercase", letterSpacing: "0.04em" };
export const td = { fontSize: 13, color: T.text, padding: "10px", borderBottom: `1px solid ${T.border}`, verticalAlign: "middle" };

export const Table = ({ head, children }) => (
  <div style={{ overflowX: "auto" }}>
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead><tr>{head.map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

// One request at a time per screen, its error shown once, its success a short
// line — the shape every form below shares.
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const run = async (fn, success = "") => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const out = await fn();
      if (success) setNotice(typeof success === "function" ? success(out) : success);
      return out;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, notice, run, setError, setNotice };
}

export const Feedback = ({ action }) => (
  <>
    {action.error && <div style={{ margin: "12px 0" }}><Note tone="red">{action.error}</Note></div>}
    {action.notice && <div style={{ margin: "12px 0" }}><Note>{action.notice}</Note></div>}
  </>
);

export const invitedLine = (out, email) => (out?.invitationSent === false
  ? `Added ${email}, but the invitation email could not be sent. Use Resend once mail is working.`
  : `Added ${email} and sent them an invitation.`);

// ── People in a firm ───────────────────────────────────────────────────────
//
// The same list serves a firm's own team (`/accounts/team`) and a super admin
// looking at any firm (`/accounts/platform/...`); `paths` says which, and
// `can` is what the session may do there.
export function PeoplePanel({ me, paths, can, title = "People", hint, firmName, look }) {
  const [users, setUsers] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", title: "", phone: "", role: "staff" });
  // The person whose name, job title and phone are open for editing. These
  // are what an inspector or another party is given to reach the file
  // handler, so every firm sets them here rather than on a form.
  const [editing, setEditing] = useState(null);
  const action = useAction();

  const load = useCallback(() => {
    accountsFetch(paths.list).then(d => setUsers(d.users)).catch(e => action.setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paths.list]);
  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    const out = await action.run(
      () => accountsFetch(paths.list, { method: "POST", body: form }),
      (o) => invitedLine(o, form.email),
    );
    if (out) { setForm({ email: "", name: "", title: "", phone: "", role: "staff" }); setAdding(false); load(); }
  };

  const userPath = (u, suffix = "") => `${paths.user}/${encodeURIComponent(u.userId)}${suffix}`;
  const saveDetails = async (e) => {
    e.preventDefault();
    const { userId, name, title, phone, email } = editing;
    const out = await action.run(() => accountsFetch(userPath({ userId }), { method: "PATCH", body: { name, title, phone } }), `Saved ${email}.`);
    if (out) { setEditing(null); load(); }
  };
  const changeRole = (u, role) => action.run(() => accountsFetch(userPath(u), { method: "PATCH", body: { role } }), `${u.email} is now ${ROLE_LABELS[role]}.`).then(load);
  const resend = (u) => action.run(() => accountsFetch(userPath(u, "/resend"), { method: "POST" }),
    (o) => (o?.invitationSent === false ? "The invitation could not be sent." : `Sent ${u.email} a new invitation.`));
  const reset = (u) => {
    if (!window.confirm(`Reset two-step sign-in for ${u.email}? They will be signed out everywhere and set it up again next time.`)) return;
    action.run(() => accountsFetch(userPath(u, "/reset-two-factor"), { method: "POST" }), `Two-step sign-in reset for ${u.email}.`).then(load);
  };
  const remove = (u) => {
    if (!window.confirm(`Remove ${u.email}${firmName ? ` from ${firmName}` : ""}? They will be signed out at once.`)) return;
    action.run(() => accountsFetch(userPath(u), { method: "DELETE" }), `Removed ${u.email}.`).then(load);
  };

  // A role this person could be given by the one asking: never above the
  // asker's own, unless the platform is asking. The route is the rule; this
  // only stops offering what it would refuse.
  const assignable = (Object.keys(ROLE_LABELS)).filter(r => can.anyRole || ["staff", "admin", "owner"].indexOf(r) <= ["staff", "admin", "owner"].indexOf(me.user.role));

  return (
    <Section look={look} title={title} hint={hint} action={can.invite && !adding && <Btn onClick={() => setAdding(true)}>Add a person</Btn>}>
      <Feedback action={action} />
      {adding && (
        <form onSubmit={invite} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, alignItems: "end",
          background: T.bg, borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div><Label htmlFor="p-email">Email</Label><input id="p-email" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={inputStyle} /></div>
          <div><Label htmlFor="p-name">Name</Label><input id="p-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} /></div>
          <div><Label htmlFor="p-title">Job title</Label><input id="p-title" value={form.title} placeholder="e.g. Senior Conveyancer" onChange={e => setForm({ ...form, title: e.target.value })} style={inputStyle} /></div>
          <div><Label htmlFor="p-phone">Phone</Label><input id="p-phone" type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} style={inputStyle} /></div>
          <div><Label htmlFor="p-role">Role</Label>
            <select id="p-role" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} style={inputStyle}>
              {assignable.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn type="submit" disabled={action.busy}>{action.busy ? "Adding…" : "Add and invite"}</Btn>
            <Btn variant="quiet" onClick={() => setAdding(false)}>Cancel</Btn>
          </div>
        </form>
      )}
      {!users ? <div style={{ fontSize: 13, color: T.textFaint }}>Loading…</div> : users.length === 0 ? (
        <div style={{ fontSize: 13, color: T.textFaint }}>Nobody yet.</div>
      ) : (
        <Table head={["Person", "Role", "Status", "Two-step", "Last sign-in", ""]}>
          {users.map(u => {
            const self = u.userId === me.user.userId;
            const canEdit = self || can.role;
            if (editing && editing.userId === u.userId) {
              return (
                <tr key={u.userId}>
                  <td style={td} colSpan={6}>
                    <form onSubmit={saveDetails} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, alignItems: "end" }}>
                      <div><Label htmlFor={`e-name-${u.userId}`}>Name</Label><input id={`e-name-${u.userId}`} value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} style={inputStyle} /></div>
                      <div><Label htmlFor={`e-title-${u.userId}`}>Job title</Label><input id={`e-title-${u.userId}`} value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} style={inputStyle} /></div>
                      <div><Label htmlFor={`e-phone-${u.userId}`}>Phone</Label><input id={`e-phone-${u.userId}`} type="tel" value={editing.phone} onChange={e => setEditing({ ...editing, phone: e.target.value })} style={inputStyle} /></div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <Btn type="submit" disabled={action.busy}>{action.busy ? "Saving…" : "Save"}</Btn>
                        <Btn variant="quiet" onClick={() => setEditing(null)}>Cancel</Btn>
                      </div>
                    </form>
                  </td>
                </tr>
              );
            }
            return (
              <tr key={u.userId}>
                <td style={td}>
                  <div style={{ fontWeight: 500 }}>{u.name || u.email}{self && <span style={{ color: T.textFaint, fontWeight: 400 }}> (you)</span>}</div>
                  {u.name && <div style={{ fontSize: 12, color: T.textSoft }}>{u.email}</div>}
                  {(u.title || u.phone) && <div style={{ fontSize: 12, color: T.textSoft }}>{[u.title, u.phone].filter(Boolean).join(" · ")}</div>}
                  {u.platformRole === "super_admin" && <div style={{ marginTop: 4 }}><Badge tone="warn">Super admin</Badge></div>}
                </td>
                <td style={td}>
                  {can.role && !self ? (
                    <select aria-label={`Role for ${u.email}`} value={u.role} disabled={action.busy}
                      onChange={e => changeRole(u, e.target.value)} style={{ ...inputStyle, width: "auto", padding: "5px 8px", fontSize: 13 }}>
                      {Object.keys(ROLE_LABELS).filter(r => assignable.includes(r) || r === u.role).map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                    </select>
                  ) : ROLE_LABELS[u.role] || u.role}
                </td>
                <td style={td}><Badge>{STATUS_LABELS[u.status] || u.status}</Badge></td>
                <td style={td}>{u.twoFactor ? "On" : <span style={{ color: T.textFaint }}>Off</span>}</td>
                <td style={td}>{formatDate(u.lastSignInAt)}</td>
                <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                  {canEdit && <Btn variant="quiet" onClick={() => setEditing({ userId: u.userId, email: u.email, name: u.name || "", title: u.title || "", phone: u.phone || "" })} disabled={action.busy}>Edit details</Btn>}
                  {can.invite && u.status === "invited" && <Btn variant="quiet" onClick={() => resend(u)} disabled={action.busy}>Resend</Btn>}
                  {can.reset && !self && u.twoFactor && <Btn variant="quiet" onClick={() => reset(u)} disabled={action.busy}>Reset two-step</Btn>}
                  {can.remove && !self && <Btn variant="quiet" onClick={() => remove(u)} disabled={action.busy} style={{ color: T.danger }}>Remove</Btn>}
                </td>
              </tr>
            );
          })}
        </Table>
      )}
    </Section>
  );
}

// ── The firm's own settings ────────────────────────────────────────────────

export function FirmSettings({ firm, canEdit, onSaved, patchPath = "/firm", allowStatus = false, look, title = "Firm", hint, showName = true }) {
  const [name, setName] = useState(firm.name);
  const action = useAction();
  useEffect(() => { setName(firm.name); }, [firm.name]);
  const save = (body, message) => action.run(() => accountsFetch(patchPath, { method: "PATCH", body }), message).then(out => out && onSaved(out.firm));

  return (
    <Section look={look} title={title} hint={hint} maxWidth={620}>
      <Feedback action={action} />
      <div style={{ display: "grid", gap: 16, maxWidth: 520 }}>
        {showName && <div>
          <Label htmlFor="firm-name">Name</Label>
          <div style={{ display: "flex", gap: 8 }}>
            <input id="firm-name" value={name} disabled={!canEdit} onChange={e => setName(e.target.value)} style={inputStyle} />
            {canEdit && name.trim() && name !== firm.name && <Btn onClick={() => save({ name }, "Saved.")} disabled={action.busy}>Save</Btn>}
          </div>
        </div>}
        <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, color: T.text, cursor: canEdit ? "pointer" : "default" }}>
          <input type="checkbox" checked={firm.requireTwoFactor} disabled={!canEdit || action.busy}
            onChange={e => save({ requireTwoFactor: e.target.checked }, e.target.checked ? "Everybody will be asked to set up two-step sign-in." : "Two-step sign-in is now optional.")} />
          <span>
            <strong style={{ fontWeight: 500 }}>Require two-step sign-in</strong>
            <span style={{ display: "block", color: T.textSoft, fontSize: 12, marginTop: 2 }}>
              Everybody in the firm uses an authenticator app as well as their email, Google or Microsoft sign-in.
            </span>
          </span>
        </label>
        {allowStatus && !firm.isDefault && (
          <div>
            {firm.status === "suspended"
              ? <Btn variant="secondary" disabled={action.busy} onClick={() => save({ status: "active" }, "The firm can sign in again.")}>Reactivate firm</Btn>
              : <Btn variant="danger" disabled={action.busy} onClick={() => window.confirm(`Suspend ${firm.name}? Nobody in it will be able to sign in.`) && save({ status: "suspended" }, "Suspended. Nobody in the firm can sign in.")}>Suspend firm</Btn>}
          </div>
        )}
      </div>
    </Section>
  );
}


// ── My security ────────────────────────────────────────────────────────────

export function SecurityPanel({ me, onChanged, look, title = "Two-step sign-in" }) {
  const [mode, setMode] = useState("");
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const action = useAction();
  const sf = me.secondFactor;

  const withCode = async (path, after) => {
    const out = await action.run(() => accountsFetch(path, { method: "POST", body: { code } }));
    if (out) { setCode(""); after(out); }
  };

  return (
    <Section look={look} title={title} maxWidth={620}
      hint={look === "settings" ? "A code from an authenticator app each time you sign in, as well as your email, Google or Microsoft." : undefined}>
      <Feedback action={action} />
      {codes ? <RecoveryCodes codes={codes} onDone={() => { setCodes(null); setMode(""); }} /> : !sf.enabled ? (
        mode === "setup"
          ? <TwoFactorSetup onDone={() => { setMode(""); onChanged(); }} />
          : (
            <div>
              <p style={{ fontSize: 13, color: T.textSoft, margin: "0 0 14px", lineHeight: 1.6 }}>
                Off. Turn it on to be asked for a code from an authenticator app each time you sign in.
              </p>
              <Btn onClick={() => setMode("setup")}>Turn on</Btn>
            </div>
          )
      ) : (
        <div>
          <p style={{ fontSize: 13, color: T.textSoft, margin: "0 0 14px", lineHeight: 1.6 }}>
            On.{Number.isFinite(me.user.recoveryCodesLeft) && ` You have ${me.user.recoveryCodesLeft} recovery code${me.user.recoveryCodesLeft === 1 ? "" : "s"} left.`}
            {sf.required && " Your firm requires it, so it cannot be turned off."}
          </p>
          {mode ? (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", maxWidth: 460 }}>
              <div style={{ flex: 1, minWidth: 160 }}>
                <Label htmlFor="sec-code">Code from your app</Label>
                <input id="sec-code" value={code} onChange={e => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" maxLength={7} style={inputStyle} />
              </div>
              <Btn disabled={action.busy || code.replace(/\s/g, "").length !== 6}
                onClick={() => (mode === "codes"
                  ? withCode("/two-factor/recovery-codes", out => { setCodes(out.recoveryCodes); onChanged(); })
                  : withCode("/two-factor/disable", () => { setMode(""); onChanged(); }))}>
                {mode === "codes" ? "New recovery codes" : "Turn off"}
              </Btn>
              <Btn variant="quiet" onClick={() => { setMode(""); setCode(""); }}>Cancel</Btn>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Btn variant="secondary" onClick={() => setMode("codes")}>New recovery codes</Btn>
              {!sf.required && <Btn variant="danger" onClick={() => setMode("disable")}>Turn off</Btn>}
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

// ── On the Settings screen ─────────────────────────────────────────────────
//
// **Only for a signed-in person.** The shared staff key is nobody's account,
// so a tab opened from WordPress with it shows nothing here — exactly as
// Settings looked before.
//
// Everybody signed in gets **Your sign-in** (who they are, two-step, sign out).
// The firm's **Users** and **Sign-in** cards are for somebody who can change
// something; staff, who could only look at the list, are not shown a card of
// things they cannot do.
const MANAGES = ["users:invite", "users:role", "users:remove", "firm:settings"];

// `/accounts/me` once per token per page, shared by the Settings cards and
// the header's account link, so opening Settings is not a second round trip.
const accountReads = new Map();
export function useAccount(token) {
  const session = isSessionToken(token);
  const [me, setMe] = useState(null);
  const refresh = useCallback((fresh = true) => {
    if (!session) return Promise.resolve(null);
    if (fresh || !accountReads.has(token)) {
      accountReads.set(token, accountsFetch("/me", { token }).then(d => d.account).catch(() => null));
    }
    return accountReads.get(token).then((account) => { setMe(account); return account; });
  }, [session, token]);
  useEffect(() => { refresh(false); }, [refresh]);
  return { me: session ? me : null, refresh };
}

export function AccountSettings({ token, onSignedOut = () => window.location.reload() }) {
  const { me, refresh } = useAccount(token);
  if (!me) return null;
  const can = (c) => me.capabilities.includes(c);

  return (
    <>
      <Section look="settings" title="Your sign-in" maxWidth={620}
        hint={`${me.user.name ? `${me.user.name} · ` : ""}${me.user.email} · ${ROLE_LABELS[me.user.role] || me.user.role} at ${me.firm.name || "the firm"}`}
        action={<Btn variant="secondary" onClick={() => signOut().then(onSignedOut)}>Sign out</Btn>}>
        {me.superAdmin && (
          <p style={{ fontSize: 12, color: T.textSoft, margin: 0 }}>
            You are a super admin. Firms and super admins are managed on the <a href="/console" style={{ color: T.text }}>platform page</a>.
          </p>
        )}
      </Section>
      <SecurityPanel look="settings" me={me} onChanged={() => refresh()} />
      {MANAGES.some(can) && (
        <>
          <PeoplePanel me={me} look="settings" title="Users"
            hint={`Who can sign in to ${me.firm.name || "the firm"}. Adding somebody emails them an invitation. A person's job title and phone are what an inspector or another party is given to reach them about a matter.`}
            firmName={me.firm.name}
            paths={{ list: "/team", user: "/team" }}
            can={{ invite: can("users:invite"), role: can("users:role"), remove: can("users:remove"), reset: can("users:mfa_reset") }} />
          <FirmSettings look="settings" title="Sign-in rules" showName={false}
            hint="How everybody in the firm signs in."
            firm={me.firm} canEdit={can("firm:settings")} onSaved={() => refresh()} />
        </>
      )}
    </>
  );
}
